import type { SagaCond } from './saga.ts';
import type { SendaDef, SendaLine } from './sendas.ts';
import { sec } from './units.ts';
import { ROCK_CHAPTER } from './sendas-barbaro-5.ts';

/**
 * The barbarian's path, La Larga Caza (docs/sendas/barbaro.md): seventy years ago Sigrun of the clan of the Horn went
 * after the ash wolf that took her son and never came back. The Grey Wolf is back, and the barbarian goes out on the
 * Long Hunt with Bera, an old woman who rows, and her stone boar Terrón, while Ivar, the clan's best hunter, runs the
 * same Hunt for the chief's horn.
 *
 * Its numbers (CAZA) and its chapters live here, apart from the other paths (docs/SENDAS.md, «Para no chocar»).
 */

/**
 * The numbers of the Long Hunt's chapter 1 (§3, §4; the sim's battle/pens.ts, battle/trail.ts, battle/howler.ts).
 * Lives of nightmares in seconds of D, blows in bp of the hero's life (docs/SENDAS.md, «Calibración»).
 */
export const CAZA = {
  /**
   * The broken corral (act 1, battle/pens.ts): twelve stone boars against the far wall, `keep` of them at least in the
   * corral at the end. The pack leaps the wall in bands of `band` (from, to) every `every` (from, to) from `first`; of
   * each band `grab` go for a boar (one more `twoBp` of the time) and drag it to the wall in `drag` (3 s, the mark
   * seen): felled before, the boar trots home at `home` a tick. `need` wolves felled win it (the wolves are `wolfD`
   * seconds of D). The Grey Wolf comes in when `grayAt` are down: over the wall at `gray` a tick, takes a boar and goes
   * (it can't be stopped). Soot crows: `n` every `every` from `first`. `hpBp`, `dmgBp`: the pack over the chapter's.
   */
  pens: {
    boars: 12,
    keep: 8,
    need: 12,
    wolfD: 3,
    first: sec(5),
    every: [sec(12), sec(15)] as [number, number],
    band: [3, 5] as [number, number],
    grab: 1,
    twoBp: 9500,
    drag: sec(3),
    reach: 650,
    home: 28,
    grayAt: 6,
    gray: 150,
    crows: { first: sec(35), every: sec(40), n: 5 },
    hpBp: 10000,
    dmgBp: 16000,
  },
  /**
   * The tracks (acts 2 and 3, battle/trail.ts; §4, «Las huellas»): the hero follows a branch within `near` of its
   * prints, a few steps of slack (`slack` points of the way); the clues read within `see` (3 hexes). At a crossing,
   * Terrón brought to it (the hero within `reach` of its middle) sniffs `sniff` and grunts toward the right one. A
   * branch of the pack followed to its end: `ambush` wolves out of the rain (by act). The rain washes each crossing out in turn,
   * the first at `first`, then one every `per`; the last washed before the end, the hunt is lost. `tracks` (act 2):
   * four crossings, slow rain, no pack. `brink` (act 3, the test): seven crossings, fast rain, the howler and the pack
   * (`band`: `at` wolves as each crossing opens, the first at `first`, and `n` every `every`), crows; each sniff of
   * Terrón brings `terronWolves` wolves after him.
   */
  trail: {
    near: 1150,
    slack: 3,
    see: 3000,
    sniff: sec(3),
    reach: 1600,
    wolfD: 3,
    tracks: { crossings: 4, first: sec(45), per: sec(25), ambush: 2, hpBp: 10000, dmgBp: 10000 },
    brink: {
      crossings: 7,
      first: sec(40),
      per: sec(22),
      ambush: 5,
      band: { first: sec(4), every: [sec(18), sec(22)] as [number, number], n: [2, 2] as [number, number], at: [3, 4] as [number, number] },
      crows: { first: sec(50), every: sec(45), n: 4 },
      terronWolves: 1,
      hpBp: 10000,
      dmgBp: 17000,
    },
  },
  /**
   * The howler (act 3, battle/howler.ts): a big wolf, `d` seconds of D, biting `biteBp` of the hero's life every
   * `reload`. Every `every` from `first` it rises on its hind legs and bristles `rear` (3 s), then howls `howl` (2.5 s):
   * `cutBp` of its life struck in the howl, or a loose stone on it, cuts it (it reels `stun`); a howl let out raises up
   * to `raise` of the pack fallen since the last and heals the pack `healBp` of its life. It leads, it doesn't stand
   * and die: `bites` bites and it backs off up the trail `back`, `keep` from the hero.
   */
  howler: { d: 20, biteBp: 1000, reload: sec(1.25), reach: 1100, every: sec(25), first: sec(16), rear: sec(3), howl: sec(2.5), cutBp: 700, stun: sec(2), raise: 4, healBp: 2500, bites: 3, back: sec(2.5), keep: 3600 },
  /**
   * The loose stones of the ledge (act 3): the hero within `reach` stands `stand` and pushes it; it rolls `roll` toward
   * the sea at `speed` a tick, `width` wide, striking what it meets for `d` seconds of D and making it reel `stun`.
   */
  stone: { stand: sec(1), reach: 1150, roll: 8500, speed: 420, width: 850, d: 3, stun: sec(2) },
};

/**
 * The numbers of the Long Hunt's chapter 2, «Las Vértebras» (§3, §4 «Los arcos»; the sim's battle/vertebras*.ts). Lives
 * in seconds of D, blows in bp of the hero's life (docs/SENDAS.md, «Calibración»).
 */
export const VERT = {
  /**
   * The arches in the wind (act 1, battle/arches.ts): a way of wet arches out over the sea, `arches` of them, a mooring
   * post on each. The gusts come along it from the side, `first`, then every `every` (from, to), seen `warn` before
   * (the foam lifting in a line): one catching the hero or a wolf in the open pushes it `push` toward the drop (sim units,
   * a hex 1000); pushed past the way's edge, a wolf is gone and the hero falls (`fallBp` of its life, `stun`, back on
   * the last post it passed). Within `post` of a post nothing moves it. The pack: `band` (from, to) wolves from ahead
   * every `every` (from, to) from `first`, `behind` from behind every `behindEvery`; crows. Terrón balks at the gap of
   * arch `balk` until the hero stands by him `push` (Bera: «Empujalo»).
   */
  arches: {
    arches: 11,
    gust: { first: sec(9), every: [sec(8), sec(11)] as [number, number], warn: sec(3), push: 2600, fallBp: 2600, stun: sec(1.2) },
    post: 850,
    wolfD: 3,
    pack: { first: sec(5), every: [sec(12), sec(16)] as [number, number], band: [4, 5] as [number, number], behind: 3, behindEvery: sec(24) },
    crows: { first: sec(30), every: sec(35), n: 3 },
    balk: 4,
    nudge: sec(1),
    nudgeReach: 1100,
    end: 1300,
    hpBp: 10000,
    dmgBp: 20000,
  },
  /**
   * The leaps (act 2, battle/leaps.ts; §4 «Los arcos»): ten arches over the sea and the last vertebra, joined by gaps,
   * each crossed by Sigrun's rope (a tap on the arch across: `rope` to swing over) or quicker by Salto or Carga. Each arch
   * bears two arrivals; at the third it falls, and the hero with it: `fallBp` of its life, back on the arch it left.
   * Three arches carry Sigrun's mark; the vertebra reached with the three touched wins it. The ground from the seed:
   * `tries` layouts at most before the one of the spec.
   */
  leaps: { rope: sec(1.6), fallBp: 1500, tries: 300, hpBp: 10000, dmgBp: 10000 },
  /**
   * The pack's den (act 3, the test, battle/den.ts): the last vertebra, wide, the cave to the north. Bera and Terrón
   * launch the boat at the south (`launch` of work, a blow on Bera stops it `flinch`); the hero in the boat with it in
   * the water wins it. The pack out of the cave: bands of `band` (from, to) every `every` (from, to) from `first`;
   * crows. Two howlers (CAZA.howler, each `howlerD`), their first howls at `firsts`; one let out while the other rears or
   * howls is double (`doubleBp` healed, twice the raised). Cracked slabs (`slabs` of three cells): two arrivals of the
   * hero, and at the third it creaks `creak` and sinks, taking what stands on it (the hero falls, `fallBp`). Four loose
   * stones on the rim roll in toward the middle (CAZA.stone). With `rush.left` of the work to go, the whole pack comes
   * out at once (`rush.n` wolves): the boat is nearly in.
   */
  den: {
    launch: sec(180),
    flinch: sec(1.5),
    first: sec(6),
    every: [sec(11), sec(15)] as [number, number],
    band: [4, 5] as [number, number],
    crows: { first: sec(40), every: sec(40), n: 3 },
    wolfD: 3,
    howlerD: 20,
    firsts: [sec(12), sec(24)] as [number, number],
    leash: 3600,
    doubleBp: 5000,
    rush: { left: sec(30), n: 6 },
    slabs: 6,
    creak: sec(1.2),
    fallBp: 1500,
    stones: 4,
    board: 900,
    hpBp: 10000,
    dmgBp: 21000,
  },
};

/**
 * The numbers of the Long Hunt's chapter 3, «El bote» (§3; the sim's battle/bote*.ts, wake.ts, swell.ts, elder.ts). Lives
 * in seconds of D, blows in bp of the hero's life (docs/SENDAS.md, «Calibración»). The boat is the same in every act:
 * six oars, eight shields on the gunwale, Bera at the tiller; a wolf aboard further than `gnawReach` from the hero gnaws
 * at the hull, a blow every `gnaw` (the boat bears 40, the acts' `boat` npc).
 */
export const BOTE = {
  boat: { gnaw: sec(2.5), gnawReach: 1600 },
  /**
   * The wake (act 1, battle/wake.ts): `forks` forks between the floes, the first seen at `first`, each `look` before the
   * boat is in it (a tap on a channel; none tapped, Bera takes the widest, as Ivar does), `pass` in it, `gap` to the
   * next. The distance to the Wolf (bp of the bar) from `start`: each second `row` an oar rowed, less `drag`. The right
   * channel `gain`s; the pack's wake loses `wrong` and brings `ambush` wolves aboard; a closing one stalls the boat
   * `stall` (no rowing), crushes `crush` blows into the hull and lets `stallWolves` climb. Wolves climb at the oars:
   * `climb.n` every `climb.every`; one at an oar `bite`s it through (its rower takes a spare in `spare`, `spares` of
   * them). Ivar always takes the widest.
   */
  wake: {
    forks: 7,
    first: sec(16),
    look: sec(13),
    pass: sec(5),
    gap: sec(24),
    start: 5000,
    row: 12,
    drag: 44,
    gain: 1200,
    wrong: 1600,
    ambush: 3,
    stall: sec(6),
    crush: 5,
    stallWolves: 3,
    climb: { first: sec(6), every: [sec(7), sec(10)] as [number, number], n: [1, 2] as [number, number] },
    bite: sec(2),
    biteReach: 750,
    spare: sec(3),
    spares: 3,
    ivar: { start: 7000, gain: 900, wrong: 1100 },
    crows: { first: sec(40), every: sec(45), n: 3 },
    wolfD: 3,
    hpBp: 10000,
    dmgBp: 47000,
  },
  /**
   * The swell (act 2, battle/swell.ts): a big wave every `every` (from, to) from `first`, the bow rising `warn` before;
   * `need` passed win it. Whatever isn't within `hold` of a shield slides `push` astern, and past the stern it's
   * overboard: a wolf gone, the hero hurt `fallBp` and back at the stern (`stun`). A person struck within `shaken` of
   * the wave loses its grip and goes over: the hero at the gunwale within `haul` of it before `haulT` hauls it back;
   * `lostMax` lost lose it. The floes off the sides, crossed to by the bow rope (`rope`, a floe within `ropeReach`):
   * bands climb onto them and leap aboard `gather` later, unless the hero is on that floe.
   */
  swell: {
    first: sec(14),
    every: [sec(26), sec(32)] as [number, number],
    warn: sec(3),
    need: 6,
    push: 3200,
    fallBp: 1600,
    stun: sec(1.2),
    hold: 850,
    haul: 900,
    haulT: sec(8),
    lostMax: 2,
    shaken: sec(4),
    rope: sec(1.3),
    ropeReach: 6500,
    climb: { first: sec(5), every: [sec(7), sec(10)] as [number, number], n: [2, 2] as [number, number] },
    floe: { first: sec(10), every: [sec(13), sec(17)] as [number, number], n: [3, 4] as [number, number], gather: sec(5) },
    crows: { first: sec(30), every: sec(40), n: 3 },
    wolfD: 3,
    hpBp: 10000,
    dmgBp: 42000,
  },
  /**
   * The old howler (act 3, the test, battle/elder.ts): `d` seconds of D, biting `biteBp` every `reload`. Each `fleeBp`
   * of its life lost it looks `look` at the nearest floe tip with dark water off it (two of four by turns, a shift
   * every `shift`), then bounds `bound` toward it (out of the hero's reach) and swims for it at the hero's pace less `slowBp` a blow; there it goes under `under` and comes back
   * with what it lost since it last came up (or was cut off) healed and `healBp` more, with `raise` of the pack. The hero within `block` of the tip as it comes, or `caught` blows on the
   * way, cut it off (it reels `stun`). Every `leapEvery` it leaps on the hero, its shadow `leapWarn` before: `leapBp`
   * within `leapR`, `leapHull` blows on the hull if it lands on it. The pack climbs out at the tips.
   */
  elder: {
    d: 115,
    biteBp: 1300,
    reload: sec(1.8),
    reach: 1150,
    fleeBp: 1500,
    look: sec(3),
    bound: 2300,
    slowBp: 800,
    block: 1000,
    caught: 4,
    stun: sec(2),
    under: sec(6),
    healBp: 1000,
    raise: 3,
    shift: sec(16),
    leapEvery: sec(12),
    leapFirst: sec(9),
    leapWarn: sec(3),
    leapBp: 2500,
    leapR: 950,
    leapHull: 2,
    pack: { first: sec(5), every: [sec(17), sec(21)] as [number, number], n: [2, 3] as [number, number] },
    crows: { first: sec(35), every: sec(40), n: 3 },
    wolfD: 3,
    hpBp: 10000,
    dmgBp: 23000,
  },
};

/**
 * The numbers of the Long Hunt's chapter 4, «La Isla Blanca» (§3, §4; the sim's battle/isla*.ts, floes.ts, lagoon.ts,
 * seal.ts). Lives in seconds of D, blows in bp of the hero's life (docs/SENDAS.md, «Calibración»).
 */
export const ISLA = {
  /**
   * The ice that moves (act 1, battle/floes.ts): the island's shore with Sigrun's camp of piled stones (the `camp` npc, 25
   * blows), three floes off it, each joined to the shore by a neck of ice that parts and joins with the water (`join`
   * joined, the crack seen `warn` before, `apart` parted; each neck its own offset from the seed). What stands on a neck
   * as it parts goes in. Every `crack.every` (from, to) from `crack.first` a crack runs through the hero's cell, `len`
   * hexes along a line, seen `warn` before; it opens `open` and takes what is on it (the hero up again, `fallBp` of its
   * life less; a nightmare, gone). Three bands come off the floes (`bands`: wolves, frost colossi), each `every` after the
   * last began and never before `gap` after it is down; each in `parts` parts `part` apart (the colossi in the first). A
   * colossus goes for the camp past the hero (a blow `colossusHit`
   * blows, every its reload; its blows on the hero `colossusDmgBp` of a colossus's); one wolf in three gnaws at it (a blow every `gnaw`) when the hero isn't within `gnawReach`.
   */
  floes: {
    bands: [
      [8, 1],
      [10, 1],
      [12, 2],
    ] as [number, number][],
    first: sec(6),
    every: sec(95),
    gap: sec(8),
    parts: 3,
    part: sec(16),
    join: sec(16),
    warn: sec(3),
    apart: sec(9),
    crack: { first: sec(12), every: [sec(12), sec(16)] as [number, number], len: 5, open: sec(2.5) },
    fallBp: 2000,
    colossusD: 14,
    colossusHit: 2,
    colossusDmgBp: 4500,
    campReach: 1400,
    gnaw: sec(2.5),
    gnawReach: 1800,
    wolfD: 3,
    hpBp: 10000,
    dmgBp: 16000,
  },
  /**
   * What walked under the ice (act 2, the puzzle, battle/lagoon.ts; §4): the clear lagoon, Sigrun's six tracks frozen
   * under it, the Wolf's and the pack's over most. A crossing reads with the hero still within `see` of it for `readT`
   * (the frost brushed off the ice: who's whole on top, who's cut); the last is the one none of hers walks over. Broken where it ends (the hero within `reach` of an end
   * for `breakT`), won. Broken elsewhere: the first time it cracks round it; after that the ice gives way, the hero in
   * the water (`fallBp`), the ice within `sinkR` open, the crossings within `lostR` gone and `sinkWolves` of the pack up out of the open water. Wolves off the shore:
   * `wolves.n` every `wolves.every` from `wolves.first`.
   */
  lagoon: {
    see: 1200,
    readT: sec(2),
    reach: 900,
    breakT: sec(1.5),
    fallBp: 2000,
    sinkR: 1300,
    lostR: 2400,
    sinkWolves: 4,
    wolves: { first: sec(18), every: sec(30), n: [3, 4] as [number, number] },
    wolfD: 3,
    hpBp: 10000,
    dmgBp: 30000,
  },
  /**
   * Sigrun's last night (act 3, the memory, the test, battle/seal.ts): her fire (the `fire` npc, 25 blows) and the Ash
   * Seal, `d` seconds of D, biting `biteBp` every `reload` within `reach`, slow on the ice (`speed` a second). Every
   * `slide.every` (from, to) from `slide.first` it stands and looks `slide.warn` at what it wants to ram (the hero within
   * `aggro`, else the fire; aiming at the fire, it turns to the hero come within half of it), the line marked `len` long
   * and `width` across; then it slides belly-down along it: the
   * hero on it takes `slide.bp`, the fire on it is out; the ice it cut takes the wolves that step on it for `slide.crack`. Up from a crack or cut off, it slides no sooner
   * than `slide.after`.
   * Each `fleeBp` of its life lost it looks `look` at the nearest open crack and goes for it; there under `under`, back
   * with what it lost healed and `healBp` more, with `raise` of the pack; cut off with the hero within `block` of the
   * crack, or `caught` blows on the way (it reels `stun`). Two howlers (`howler`: rear, howl, `cutBp` struck in it cuts
   * it, else `raise` wolves). The pack: `pack.n` every `pack.every` from `pack.first`; one in three gnaws at the fire.
   */
  seal: {
    d: 135,
    biteBp: 1500,
    reload: sec(1.6),
    reach: 1400,
    speed: 1100,
    aggro: 8000,
    slide: { first: sec(8), every: [sec(9), sec(12)] as [number, number], after: sec(6), warn: sec(3), len: 10000, width: 950, bp: 3000, crack: sec(6), pace: 900 },
    fleeBp: 2500,
    look: sec(3),
    block: 1200,
    caught: 4,
    stun: sec(2),
    under: sec(5),
    healBp: 1000,
    raise: 2,
    howler: { d: 16, first: sec(14), every: sec(26), rear: sec(3), howl: sec(2.5), cutBp: 1500, raise: 3, stun: sec(2) },
    pack: { first: sec(6), every: [sec(16), sec(20)] as [number, number], n: [2, 3] as [number, number] },
    fireReach: 1400,
    gnaw: sec(3),
    gnawReach: 1800,
    wolfD: 3,
    hpBp: 10000,
    dmgBp: 10000,
  },
};

const ln = (id: string, who: string, cond?: SagaCond): SendaLine => (cond ? { id, who, if: cond } : { id, who });

/** The group never played El Cuerno: Brann brings Ulla in (docs/sendas/barbaro.md §2, «El clan, con cuidado»). */
const NO_HORN: SagaCond = { notFlag: '@horn.met' };

/**
 * Chapter 1, «Las huellas» (level 18): a night of rain on the tail's pastures, a grey shadow over the corral's wall
 * (the cinematic); the clan's council round the fire (Ulla, Ivar, Bera and Terrón); the broken corral (twelve wolves
 * of the pack down, the boars kept, the Grey Wolf taking one over the wall); the tracks at the wall; the rain that
 * washes them out (four crossings, a clue each); the campfire in a shepherds' cave with Bera; and the test, the ledge
 * over the sea (seven crossings, fast rain, the howler and its howl to cut); closing where the tracks end at the edge,
 * a grey head swimming out to sea, and Ivar sitting on a stone, wet and smiling.
 */
export const BARBARIAN_PATH: SendaDef = {
  id: 'barbarian',
  cls: 'barbarian',
  planned: 5,
  cast: {
    ulla: { npc: 'ulla' },
    brann: { npc: 'brann' },
    bera: { model: 'bera' },
    crone: { model: 'bera' },
    terron: { model: 'terron' },
    ivar: { model: 'ivar' },
    stranger: { model: 'ivar' },
    // Ivar's two hunters (the close).
    hunter: { cls: 'barbarian', tier: 2 },
    hunter2: { cls: 'barbarian', tier: 1 },
    // Sten, Ivar's younger brother (chapter 2 on): «el remero» until Bera names him.
    sten: { model: 'sten' },
    rower: { model: 'sten' },
    // The four of the clan at the oars of Bera's boat (chapter 3 on), and Ulla's people on the shore.
    rower1: { cls: 'barbarian', tier: 1 },
    rower2: { cls: 'barbarian', tier: 2 },
    rower3: { cls: 'barbarian', tier: 2 },
    rower4: { cls: 'barbarian', tier: 1 },
    // Chapter 4: Sigrun as she was the thirty-first night (the memory's face), and her diary (a voice on the screen).
    sigrun: { model: 'sigrun' },
    diary: { voice: true },
  },
  chapters: [
    {
      id: 'tracks',
      closedBy: 'bera',
      piece: 'huntStripe',
      acts: [
        {
          // A night of rain on the tail's pastures: the stone pens, mud to the ankles; a grey shadow leaps the wall
          // without touching it, and a stone boar squeals once.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-barbarian-1',
            place: 'cazaNight',
            sky: 'night',
            weather: 'rain',
            cam: [
              { from: [-5.5, 8.5, 2.4], at: [0, 0, 0.5], ms: 5200 },
              { from: [-2.5, 3.6, 1.2], at: [0.4, -3.0, 0.6], ms: 5200 },
              { from: [1.5, -1.0, 0.9], at: [-0.5, -5.6, 1.1], ms: 5200 },
            ],
            figures: [],
          },
        },
        {
          // The clan's council round the fire, in the Hall (or the yard where there is none).
          kind: 'scene',
          id: 'open',
          lines: [
            ln('l1', 'brann', NO_HORN),
            ln('l2', 'ulla'),
            ln('l3', 'ulla'),
            ln('l4', 'narrator'),
            ln('l5', 'stranger'),
            ln('l6', 'ulla'),
            ln('l7', 'ivar'),
            ln('l8', 'ulla'),
            ln('l9', 'narrator'),
            ln('l10', 'crone'),
            ln('l11', 'ulla'),
            ln('l12', 'bera'),
          ],
          cine: {
            place: 'cazaFire',
            sky: 'night',
            cam: [
              { from: [-3.4, 5.8, 2.4], at: [0.2, -0.4, 0.7], ms: 9000 },
              { from: [-1.6, 2.6, 1.15], at: [0.6, -0.2, 0.6], ms: 9000 },
              { from: [2.2, 1.6, 1.1], at: [-0.6, 0.4, 0.55], ms: 9000 },
            ],
            figures: [
              { who: 'ulla', q: -1, r: -1, facing: 1.3, pose: 'stand' },
              { who: 'brann', q: -2, r: 0, facing: 0.4, pose: 'stand' },
              { who: 'ivar', q: 1, r: -2, facing: 2.1, pose: 'stand' },
              { who: 'bera', q: 2, r: 1, facing: 3.6, pose: 'stand' },
              { who: 'terron', q: 3, r: 0, facing: 3.4, pose: 'stand' },
              { who: 'hero', q: 0, r: 2, facing: 4.6, pose: 'stand' },
            ],
            fire: { q: 0, r: 0 },
            structures: [{ type: 'hall', q: 0, r: -4 }],
            alt: {
              unless: 'hall',
              cine: {
                place: 'cazaFire',
                sky: 'night',
                cam: [
                  { from: [-3.4, 5.8, 2.4], at: [0.2, -0.4, 0.7], ms: 9000 },
                  { from: [-1.6, 2.6, 1.15], at: [0.6, -0.2, 0.6], ms: 9000 },
                  { from: [2.2, 1.6, 1.1], at: [-0.6, 0.4, 0.55], ms: 9000 },
                ],
                figures: [
                  { who: 'ulla', q: -1, r: -1, facing: 1.3, pose: 'stand' },
                  { who: 'brann', q: -2, r: 0, facing: 0.4, pose: 'stand' },
                  { who: 'ivar', q: 1, r: -2, facing: 2.1, pose: 'stand' },
                  { who: 'bera', q: 2, r: 1, facing: 3.6, pose: 'stand' },
                  { who: 'terron', q: 3, r: 0, facing: 3.4, pose: 'stand' },
                  { who: 'hero', q: 0, r: 2, facing: 4.6, pose: 'stand' },
                ],
                fire: { q: 0, r: 0 },
              },
            },
          },
        },
        {
          // The broken corral by night, in the rain: the pack over the wall, the herd against the far side (§3, act 1).
          kind: 'fight',
          id: 'pens',
          mode: 'pens',
          place: 'cazaPens',
          night: true,
          weather: 'rain',
          budgetBp: 9000,
          goal: { kind: 'herd' },
          npcs: [
            { id: 'bera', does: 'wait', hits: 30, safe: true, vital: false },
            { id: 'terron', does: 'wait', hits: 30, safe: true, vital: false },
          ],
          says: [
            { on: 'start', who: 'bera', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'grab1', who: 'bera', line: 'l2' },
            { on: 'cue', cue: 'saved1', who: 'bera', line: 'l3' },
            { on: 'cue', cue: 'gray', who: 'bera', line: 'l4', delay: sec(1) },
            { on: 'cue', cue: 'taken1', who: 'bera', line: 'l5' },
          ],
        },
        {
          // At the wall: tracks in the mud, many, the pack's and his; beyond, boots: Ivar is out already.
          kind: 'scene',
          id: 'wall',
          lines: [ln('l1', 'narrator'), ln('l2', 'bera'), ln('l3', 'bera')],
          cine: {
            place: 'cazaPens',
            sky: 'night',
            weather: 'rain',
            cam: [
              { from: [-2.6, -1.6, 1.6], at: [0.4, -5.4, 0.2], ms: 8000 },
              { from: [1.2, -3.2, 0.85], at: [-0.2, -6.4, 0.1], ms: 9000 },
            ],
            figures: [
              { who: 'bera', q: 1, r: -5, facing: 4.2, pose: 'kneel' },
              { who: 'terron', q: 2, r: -5, facing: 4.6, pose: 'stand' },
              { who: 'hero', q: -1, r: -4, facing: 4.9, pose: 'stand' },
            ],
          },
        },
        {
          // The rain that washes: the Grey Wolf's tracks across the pastures, four crossings, one clue each (§4).
          kind: 'fight',
          id: 'rain',
          mode: 'tracks',
          place: 'cazaMoor',
          night: true,
          weather: 'rain',
          budgetBp: 9000,
          goal: { kind: 'trail' },
          npcs: [
            { id: 'bera', does: 'follow', hits: 30, safe: true, vital: false, follow: 2.2 },
            { id: 'terron', does: 'follow', hits: 24, vital: false, follow: 1.4 },
          ],
          says: [
            { on: 'start', who: 'bera', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'jump', who: 'bera', line: 'l2' },
            { on: 'cue', cue: 'pit', who: 'bera', line: 'l3' },
            { on: 'cue', cue: 'drink', who: 'bera', line: 'l4' },
            { on: 'cue', cue: 'wrong1', who: 'bera', line: 'l5', delay: sec(1) },
            { on: 'cue', cue: 'grunt1', who: 'bera', line: 'l6' },
            { on: 'cue', cue: 'washed1', who: 'bera', line: 'l7' },
          ],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['bera', 'terron'],
          questions: [
            { id: 'sigrun', lines: [ln('l1', 'bera'), ln('l2', 'bera')], set: ['askedSigrun'] },
            { id: 'ivar', lines: [ln('l1', 'bera'), ln('l2', 'bera')], set: ['askedIvar'] },
            { id: 'why', lines: [ln('l1', 'bera'), ln('l2', 'bera')], set: ['askedWhyBera'] },
          ],
          cine: {
            place: 'cazaCave',
            sky: 'night',
            weather: 'rain',
            cam: [
              { from: [2.6, 3.2, 1.9], at: [-0.3, -0.4, 0.4], ms: 9000 },
              { from: [-1.8, 3.0, 1.3], at: [0.2, -0.3, 0.45], ms: 9000 },
            ],
            figures: [
              { who: 'bera', q: -1, r: 1, facing: 5.3, pose: 'sit' },
              { who: 'terron', q: -1, r: 0, facing: 0.2, pose: 'lie' },
              { who: 'hero', q: 1, r: -1, facing: 2.2, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          // The test: the ledge over the sea in a slanting rain, seven crossings, the howler and the pack (§3, act 3).
          kind: 'fight',
          id: 'brink',
          mode: 'brink',
          place: 'cazaBrink',
          night: true,
          weather: 'rain',
          budgetBp: 9000,
          goal: { kind: 'trail' },
          npcs: [
            { id: 'bera', does: 'follow', hits: 30, safe: true, vital: false, follow: 2.4 },
            { id: 'terron', does: 'follow', hits: 24, vital: false, follow: 1.4 },
          ],
          says: [
            { on: 'start', who: 'bera', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'rear1', who: 'bera', line: 'l2' },
            { on: 'cue', cue: 'cut1', who: 'bera', line: 'l3' },
            { on: 'cue', cue: 'howled1', who: 'bera', line: 'l4', delay: sec(1) },
            { on: 'cue', cue: 'grunt1', who: 'bera', line: 'l5', delay: sec(1) },
            { on: 'cue', cue: 'stone1', who: 'bera', line: 'l6' },
            { on: 'cue', cue: 'howlerDown', who: 'bera', line: 'l7' },
          ],
          trial: true,
        },
        {
          // Where the tracks end at the edge: a grey head swimming out to sea; up on the ledge, Ivar with two hunters.
          kind: 'scene',
          id: 'close',
          lines: [ln('l1', 'narrator'), ln('l2', 'bera'), ln('l3', 'narrator'), ln('l4', 'ivar'), ln('l5', 'ivar'), ln('l6', 'bera'), ln('l7', 'narrator')],
          cine: {
            place: 'cazaEnd',
            sky: 'night',
            weather: 'rain',
            cam: [
              // Down over the edge to the dark water and the grey head in it; then up the ledge to Ivar on his stone.
              { from: [-1.6, 3.0, 2.6], at: [3.6, -1.2, -2.6], ms: 9000 },
              { from: [-0.6, 2.6, 1.3], at: [-1.2, -1.6, 0.7], ms: 9000 },
              { from: [1.4, 1.2, 1.0], at: [-1.4, -1.2, 0.6], ms: 9000 },
            ],
            figures: [
              { who: 'ivar', q: -2, r: -1, facing: 0.6, pose: 'sit' },
              { who: 'hunter', q: -3, r: 0, facing: 0.3, pose: 'stand' },
              { who: 'hunter2', q: -2, r: -2, facing: 0.9, pose: 'stand' },
              { who: 'bera', q: 1, r: 0, facing: 0.1, pose: 'stand' },
              { who: 'terron', q: 1, r: 1, facing: 0.2, pose: 'stand' },
              { who: 'hero', q: -1, r: 3, facing: 4.4, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      id: 'vertebrae',
      closedBy: 'bera',
      piece: 'clanBracer',
      acts: [
        {
          // The tail goes into the sea, and out of it arches of stone one after another, lower and lower, like the
          // vertebrae of a beast going under; waves bursting between them; below by the shore, Ivar's red-prowed boat.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-barbarian-2',
            place: 'vertSea',
            sky: 'day',
            weather: 'wind',
            cam: [
              { from: [-6.0, 12.0, 4.2], at: [0, 0, 0.4], ms: 5600 },
              { from: [4.5, 6.0, 2.2], at: [-1.0, -4.0, 0.3], ms: 5600 },
              { from: [6.5, 2.0, 0.6], at: [4.0, -2.0, -1.4], ms: 5200 },
            ],
            figures: [],
          },
        },
        {
          // The tip of the tail: the first arch going out over the water.
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'bera'), ln('l2', 'bera'), ln('l3', 'bera')],
          cine: {
            place: 'vertTip',
            sky: 'day',
            weather: 'wind',
            cam: [
              { from: [-2.6, 6.2, 2.0], at: [0.4, 0.2, 0.6], ms: 9000 },
              { from: [2.4, 4.4, 1.2], at: [-0.4, -1.6, 0.6], ms: 9000 },
            ],
            figures: [
              { who: 'bera', q: 0, r: 1, facing: 4.7, pose: 'stand' },
              { who: 'terron', q: 1, r: 1, facing: 4.4, pose: 'stand' },
              { who: 'hero', q: -1, r: 2, facing: 4.9, pose: 'stand' },
            ],
          },
        },
        {
          // The first arches in the wind: the gusts seen coming in the foam, the posts to hold to (§3, act 1).
          kind: 'fight',
          id: 'arches',
          mode: 'arches',
          place: 'vertArches',
          weather: 'wind',
          budgetBp: 10000,
          goal: { kind: 'way' },
          npcs: [
            { id: 'bera', does: 'follow', hits: 30, safe: true, vital: false, follow: 1.6 },
            { id: 'terron', does: 'follow', hits: 24, safe: true, vital: false, follow: 1.1 },
          ],
          says: [
            { on: 'start', who: 'bera', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'warn1', who: 'bera', line: 'l2' },
            { on: 'cue', cue: 'fall1', who: 'bera', line: 'l3', delay: sec(1) },
            { on: 'cue', cue: 'over1', who: 'bera', line: 'l4' },
            { on: 'cue', cue: 'balk', who: 'bera', line: 'l5' },
            { on: 'cue', cue: 'nudged', who: 'bera', line: 'l6' },
            { on: 'cue', cue: 'mark', who: 'bera', line: 'l7' },
          ],
        },
        {
          // Below: Ivar's boat caught between two rocks, the swell on it, wolves swimming at it; Sigrun's old rope.
          kind: 'choice',
          id: 'below',
          lines: [ln('l1', 'narrator'), ln('l2', 'bera'), ln('l3', 'narrator')],
          options: [
            { id: 'rope', set: ['ropeDropped'] },
            { id: 'watch', set: [] },
          ],
          cine: {
            place: 'vertBelow',
            sky: 'day',
            weather: 'wind',
            cam: [
              { from: [-1.6, 1.8, 3.6], at: [3.4, -2.0, -2.0], ms: 9000 },
              { from: [-0.6, 0.4, 3.0], at: [3.8, -2.4, -2.2], ms: 9000 },
            ],
            figures: [
              { who: 'bera', q: 0, r: -1, facing: 0.2, pose: 'stand' },
              { who: 'terron', q: -1, r: -1, facing: 0.1, pose: 'stand' },
              { who: 'hero', q: 1, r: 0, facing: 0.4, pose: 'stand' },
            ],
          },
        },
        {
          // The young rower comes up to the arch (by the rope, or on his own hands); Ivar frees the boat alone.
          kind: 'scene',
          id: 'sten',
          lines: [
            ln('l1', 'narrator', { flag: 'ropeDropped' }),
            ln('l2', 'narrator', { notFlag: 'ropeDropped' }),
            ln('l3', 'ivar'),
            ln('l4', 'rower', { flag: 'ropeDropped' }),
            ln('l5', 'rower', { notFlag: 'ropeDropped' }),
            ln('l6', 'bera'),
            ln('l7', 'sten'),
          ],
          cine: {
            place: 'vertBelow',
            sky: 'day',
            weather: 'wind',
            cam: [
              { from: [-1.6, 1.8, 3.6], at: [3.4, -2.0, -2.0], ms: 9000 },
              { from: [-3.4, 3.4, 1.6], at: [0.4, -0.6, 0.6], ms: 9000 },
            ],
            figures: [
              { who: 'bera', q: 0, r: -1, facing: 0.2, pose: 'stand' },
              { who: 'terron', q: -1, r: -1, facing: 0.1, pose: 'stand' },
              { who: 'hero', q: 1, r: 0, facing: 0.4, pose: 'stand' },
              { who: 'sten', q: -1, r: 0, facing: 0.4, pose: 'kneel' },
            ],
          },
        },
        {
          // The leaps: ten arches over the sea, each bearing two arrivals, Sigrun's three marks (§4, «Los arcos»).
          kind: 'fight',
          id: 'leaps',
          mode: 'leaps',
          place: 'vertLeaps',
          weather: 'wind',
          budgetBp: 10000,
          goal: { kind: 'leaps' },
          npcs: [],
          says: [
            { on: 'start', who: 'bera', line: 'l1', delay: sec(1) },
            { on: 'cue', cue: 'mark1', who: 'bera', line: 'l2' },
            { on: 'cue', cue: 'crack2', who: 'bera', line: 'l3' },
            { on: 'cue', cue: 'fall1', who: 'bera', line: 'l4', delay: sec(1) },
            { on: 'cue', cue: 'moved1', who: 'bera', line: 'l5', delay: sec(1) },
            { on: 'cue', cue: 'mark3', who: 'bera', line: 'l6' },
            { on: 'cue', cue: 'stuck', who: 'bera', line: 'l7' },
          ],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['bera', 'terron', 'sten'],
          questions: [
            { id: 'sent', lines: [ln('l1', 'bera'), ln('l2', 'bera'), ln('l3', 'bera')], set: ['askedSentBack'] },
            { id: 'looks', lines: [ln('l1', 'bera'), ln('l2', 'bera')], set: ['askedLooks'] },
            { id: 'song', lines: [ln('l1', 'bera'), ln('l2', 'bera')], set: ['askedSong'] },
          ],
          cine: {
            place: 'vertFire',
            sky: 'dusk',
            weather: 'wind',
            cam: [
              { from: [2.6, 3.4, 1.8], at: [-0.3, -0.4, 0.5], ms: 9000 },
              { from: [-2.0, 3.0, 1.2], at: [0.3, -0.6, 0.5], ms: 9000 },
            ],
            figures: [
              { who: 'bera', q: -1, r: 1, facing: 5.3, pose: 'sit' },
              { who: 'terron', q: -2, r: 1, facing: 0.2, pose: 'lie' },
              { who: 'sten', q: 1, r: 0, facing: 3.0, pose: 'sit' },
              { who: 'hero', q: 1, r: -1, facing: 2.2, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          // The test: the last vertebra, the pack out of its cave, two howlers, the cracked slabs; Bera launching the boat.
          kind: 'fight',
          id: 'den',
          mode: 'den',
          place: 'vertDen',
          budgetBp: 10000,
          goal: { kind: 'boat' },
          npcs: [
            { id: 'bera', does: 'wait', hits: 20, vital: true },
            { id: 'terron', does: 'wait', hits: 24, safe: true, vital: false },
            { id: 'sten', does: 'wait', hits: 24, safe: true, vital: false },
          ],
          says: [
            { on: 'start', who: 'bera', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'rear1', who: 'bera', line: 'l2' },
            { on: 'cue', cue: 'double', who: 'bera', line: 'l3', delay: sec(1) },
            { on: 'cue', cue: 'sunk1', who: 'bera', line: 'l4' },
            { on: 'npcHit', npc: 'bera', who: 'bera', line: 'l5' },
            { on: 'half', who: 'sten', line: 'l6' },
            { on: 'cue', cue: 'launched', who: 'bera', line: 'l7' },
          ],
          trial: true,
        },
        {
          // Sten sits on the bow bench of Bera's boat without being told; far off, Ivar's red prow in the Wolf's wake.
          kind: 'scene',
          id: 'close',
          lines: [ln('l1', 'narrator'), ln('l2', 'sten'), ln('l3', 'bera'), ln('l4', 'narrator'), ln('l5', 'bera'), ln('l6', 'narrator')],
          cine: {
            place: 'vertClose',
            sky: 'dusk',
            cam: [
              { from: [-3.6, 5.6, 2.8], at: [0.2, -0.2, 0.2], ms: 9000 },
              { from: [3.6, 3.4, 1.8], at: [-0.4, -0.6, 0.4], ms: 9000 },
              { from: [1.4, 2.4, 1.6], at: [-3.0, -8.0, 0.0], ms: 9000 },
            ],
            figures: [
              { who: 'sten', q: 1, r: -1, facing: 3.6, pose: 'sit' },
              { who: 'bera', q: -1, r: 1, facing: 0.6, pose: 'sit' },
              { who: 'terron', q: 0, r: 1, facing: 0.4, pose: 'lie' },
              { who: 'hero', q: 0, r: 0, facing: 0.5, pose: 'sit' },
            ],
          },
        },
      ],
    },
    {
      id: 'boat',
      closedBy: 'bera',
      piece: 'waxCloak',
      acts: [
        {
          // The tail's tip from the sea: the clan's camp coming down, carts loaded, hides tied, the herd in a line toward
          // the flank; Ulla on the shore watching the long boat go, six at the oars, shields on the gunwale, a boar's head.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-barbarian-3',
            place: 'boteCamp',
            sky: 'dusk',
            cam: [
              // Over the camp coming down on the shore, out to the boat on the water; along the herd's line; the boat going.
              { from: [-9.0, 17.0, 3.2], at: [2.0, -2.0, 0.2], ms: 5600 },
              { from: [3.0, 12.0, 1.6], at: [12.0, 7.0, 0.3], ms: 5600 },
              { from: [-7.0, 10.0, 1.4], at: [3.0, -3.0, 0.4], ms: 5200 },
            ],
            figures: [],
          },
        },
        {
          // On the shore by the boat, Ulla seeing them off; the camp coming down behind her.
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'ulla'), ln('l2', 'ulla'), ln('l3', 'ulla'), ln('l4', 'bera'), ln('l5', 'sten'), ln('l6', 'bera')],
          cine: {
            place: 'boteShore',
            sky: 'dusk',
            cam: [
              // From the bow back over the boat to Ulla on the shore and the camp behind her; then close on the boat.
              { from: [1.6, -6.0, 2.4], at: [-2.6, 6.0, 0.6], ms: 9000 },
              { from: [-6.5, 9.5, 1.5], at: [1.0, 0.0, 0.5], ms: 9000 },
            ],
            figures: [
              { who: 'ulla', q: -4, r: 8, facing: 4.7, pose: 'stand' },
              { who: 'bera', q: 1, r: -1, facing: 2.4, pose: 'stand' },
              { who: 'sten', q: 1, r: 0, facing: 2.6, pose: 'sit' },
              { who: 'terron', q: 0, r: -1, facing: 2.2, pose: 'stand' },
              { who: 'hero', q: 0, r: 1, facing: 1.8, pose: 'stand' },
            ],
          },
        },
        {
          // The wake: the boat after the Wolf through drifting floes, the forks, the oars bitten (§3, act 1).
          kind: 'fight',
          id: 'wake',
          mode: 'wake',
          place: 'boteWake',
          weather: 'wind',
          budgetBp: 10000,
          goal: { kind: 'wake' },
          npcs: [
            { id: 'boat', does: 'wait', hits: 40, safe: true, vital: true, prop: true },
            { id: 'bera', does: 'wait', hits: 30, safe: true, vital: false },
            { id: 'sten', does: 'wait', hits: 24, safe: true, vital: false },
            { id: 'terron', does: 'wait', hits: 24, safe: true, vital: false },
            { id: 'rower1', does: 'wait', hits: 20, safe: true, vital: false },
            { id: 'rower2', does: 'wait', hits: 20, safe: true, vital: false },
            { id: 'rower3', does: 'wait', hits: 20, safe: true, vital: false },
            { id: 'rower4', does: 'wait', hits: 20, safe: true, vital: false },
          ],
          says: [
            { on: 'start', who: 'bera', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'fork1', who: 'bera', line: 'l2' },
            { on: 'cue', cue: 'wrong', who: 'bera', line: 'l3', delay: sec(1) },
            { on: 'cue', cue: 'stall', who: 'bera', line: 'l4', delay: sec(1) },
            { on: 'cue', cue: 'oar1', who: 'sten', line: 'l5' },
            { on: 'cue', cue: 'passIvar', who: 'ivar', line: 'l6' },
            { on: 'cue', cue: 'right2', who: 'bera', line: 'l7', delay: sec(1) },
            { on: 'cue', cue: 'lastSpare', who: 'bera', line: 'l8' },
            { on: 'cue', cue: 'bank', who: 'bera', line: 'l9' },
          ],
        },
        {
          // The calm: the Wolf going off among the floes; a bigger, grizzled wolf up on a floe, looking at the boat.
          kind: 'scene',
          id: 'calm',
          lines: [ln('l1', 'narrator'), ln('l2', 'bera')],
          cine: {
            place: 'boteCalm',
            sky: 'dusk',
            cam: [
              { from: [-1.6, 2.6, 2.8], at: [2.6, -2.6, 0.4], ms: 9000 },
              { from: [0.4, 1.6, 1.8], at: [3.2, -3.4, 0.6], ms: 9000 },
            ],
            figures: [
              { who: 'bera', q: 0, r: 1, facing: 0.9, pose: 'stand' },
              { who: 'sten', q: -1, r: 0, facing: 0.8, pose: 'sit' },
              { who: 'hero', q: 0, r: -1, facing: 0.8, pose: 'stand' },
            ],
          },
        },
        {
          // The swell: a storm on the boat, the big waves, the shields to hold, the bow rope to the floes (§3, act 2).
          kind: 'fight',
          id: 'swell',
          mode: 'swell',
          place: 'boteSwell',
          weather: 'storm',
          night: true,
          budgetBp: 10000,
          goal: { kind: 'waves' },
          npcs: [
            { id: 'boat', does: 'wait', hits: 40, safe: true, vital: true, prop: true },
            { id: 'bera', does: 'wait', hits: 30, vital: false },
            { id: 'sten', does: 'wait', hits: 24, vital: false },
            { id: 'terron', does: 'wait', hits: 24, safe: true, vital: false },
            { id: 'rower1', does: 'wait', hits: 20, vital: false },
            { id: 'rower2', does: 'wait', hits: 20, vital: false },
            { id: 'rower3', does: 'wait', hits: 20, vital: false },
            { id: 'rower4', does: 'wait', hits: 20, vital: false },
          ],
          says: [
            { on: 'start', who: 'bera', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'warn1', who: 'bera', line: 'l2' },
            { on: 'cue', cue: 'floe1', who: 'sten', line: 'l3' },
            { on: 'cue', cue: 'floe1', who: 'bera', line: 'l4', delay: sec(2) },
            { on: 'cue', cue: 'swept1', who: 'bera', line: 'l5' },
            { on: 'cue', cue: 'haul', who: 'sten', line: 'l6' },
            { on: 'cue', cue: 'over1', who: 'bera', line: 'l7', delay: sec(1) },
            { on: 'cue', cue: 'lost1', who: 'bera', line: 'l8', delay: sec(1) },
            { on: 'cue', cue: 'fall1', who: 'bera', line: 'l9', delay: sec(1) },
          ],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['bera', 'sten', 'terron'],
          questions: [
            { id: 'sten', lines: [ln('l1', 'sten'), ln('l2', 'sten'), ln('l3', 'sten', { flag: 'ropeDropped' })], set: ['askedSten'] },
            { id: 'terron', lines: [ln('l1', 'bera'), ln('l2', 'bera'), ln('l3', 'sten')], set: ['askedTerron'] },
            { id: 'want', lines: [ln('l1', 'bera')], set: ['askedBeraWant'] },
          ],
          cine: {
            place: 'boteFire',
            sky: 'night',
            cam: [
              { from: [2.4, 3.4, 1.9], at: [-0.2, -0.4, 0.5], ms: 9000 },
              { from: [-2.0, 2.8, 1.2], at: [0.3, -0.5, 0.5], ms: 9000 },
            ],
            figures: [
              { who: 'bera', q: 0, r: 1, facing: 4.7, pose: 'sit' },
              { who: 'sten', q: 0, r: -1, facing: 1.6, pose: 'sit' },
              { who: 'terron', q: 1, r: 0, facing: 3.2, pose: 'lie' },
              { who: 'hero', q: -1, r: 0, facing: 0.2, pose: 'sit' },
            ],
          },
        },
        {
          // The test: storm by night, lightning; the old howler among the floes, the dark water it heals in (§3, act 3).
          kind: 'fight',
          id: 'elder',
          mode: 'elder',
          place: 'boteElder',
          weather: 'storm',
          night: true,
          budgetBp: 10000,
          goal: { kind: 'elder' },
          npcs: [
            { id: 'boat', does: 'wait', hits: 40, safe: true, vital: true, prop: true },
            { id: 'bera', does: 'wait', hits: 30, safe: true, vital: false },
            { id: 'sten', does: 'wait', hits: 24, safe: true, vital: false },
            { id: 'terron', does: 'wait', hits: 24, safe: true, vital: false },
            { id: 'rower1', does: 'wait', hits: 20, safe: true, vital: false },
            { id: 'rower2', does: 'wait', hits: 20, safe: true, vital: false },
            { id: 'rower3', does: 'wait', hits: 20, safe: true, vital: false },
            { id: 'rower4', does: 'wait', hits: 20, safe: true, vital: false },
          ],
          says: [
            { on: 'start', who: 'bera', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'look1', who: 'bera', line: 'l2' },
            { on: 'cue', cue: 'dive1', who: 'bera', line: 'l3', delay: sec(1) },
            { on: 'cue', cue: 'cut1', who: 'bera', line: 'l4' },
            { on: 'cue', cue: 'leap1', who: 'bera', line: 'l5' },
            { on: 'half', who: 'sten', line: 'l6' },
            { on: 'cue', cue: 'elderDown', who: 'bera', line: 'l7' },
          ],
          trial: true,
        },
        {
          // A white dawn: an island of rock under ice, high and still. Bera stops rowing; the truth; Ivar's boat beached.
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'bera'),
            ln('l3', 'bera'),
            ln('l5', 'bera'),
            ln('l6', 'sten'),
            ln('l7', 'bera'),
            ln('l4', 'bera', { flag: 'askedSong' }),
            ln('l8', 'narrator'),
            ln('l9', 'narrator'),
          ],
          cine: {
            place: 'boteDawn',
            sky: 'dawn',
            cam: [
              { from: [-3.4, 5.4, 2.6], at: [0.6, -3.0, 0.8], ms: 9000 },
              { from: [2.2, 2.6, 1.3], at: [-0.4, -0.2, 0.5], ms: 9000 },
              { from: [0.6, 2.0, 1.8], at: [-1.6, -9.0, 0.6], ms: 9000 },
            ],
            figures: [
              { who: 'bera', q: 0, r: 1, facing: 4.7, pose: 'sit' },
              { who: 'sten', q: 0, r: -1, facing: 1.6, pose: 'sit' },
              { who: 'terron', q: 1, r: 1, facing: 4.4, pose: 'lie' },
              { who: 'hero', q: -1, r: 0, facing: 0.6, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      // Chapter 4, «La Isla Blanca» (level 36): an island of rock under ice, still in the sea; Sigrun's camp of piled
      // stones on its shore, the stone with thirty-one strokes, a frozen lagoon so clear the bottom shows. The ice that
      // moves (the floes); what walked under the ice (the puzzle); the diary; the fire under the aurora with Bera alone;
      // the memory, Sigrun's last night, played as her; the last page; Ivar takes the boats and Sten (the lowest point);
      // the axe (the path's one decision); Bera stays, and Sten comes back alone at dawn.
      id: 'island',
      closedBy: 'bera',
      piece: 'sigrunPaint',
      acts: [
        {
          // The island under ice, still in the sea; the camp of piled stones, the strokes on a stone, a broken harpoon in
          // frost; the lagoon behind, so clear the bottom shows; Ivar and his hunters hacking at it anywhere.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-barbarian-4',
            place: 'islaShore',
            sky: 'day',
            cam: [
              { from: [-9.0, 16.0, 7.0], at: [0, -2.0, 0.4], ms: 5600 },
              { from: [2.6, 6.4, 1.3], at: [-0.4, 4.4, 0.4], ms: 5200 },
              { from: [-4.0, 1.0, 2.4], at: [3.0, -6.0, 0.2], ms: 5600 },
            ],
            figures: [
              { who: 'ivar', q: 3, r: -6, facing: 4.0, pose: 'fight' },
              { who: 'hunter', q: 5, r: -7, facing: 3.4, pose: 'fight' },
              { who: 'hunter2', q: 1, r: -7, facing: 0.6, pose: 'fight' },
            ],
          },
        },
        {
          // On the shore by the camp: Bera counting the strokes on the stone.
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'bera'), ln('l2', 'bera'), ln('l3', 'ivar'), ln('l4', 'bera')],
          cine: {
            place: 'islaShore',
            sky: 'day',
            cam: [
              { from: [0.2, 1.4, 3.4], at: [1.8, 5.0, 0.5], ms: 9000 },
              { from: [4.2, 2.0, 2.6], at: [1.6, 5.0, 0.5], ms: 9000 },
              { from: [-3.0, 8.8, 3.7], at: [2.4, -5.6, 0.4], ms: 9000 },
            ],
            figures: [
              { who: 'bera', q: -1, r: 5, facing: 1.2, pose: 'kneel' },
              { who: 'terron', q: -2, r: 6, facing: 0.8, pose: 'stand' },
              { who: 'sten', q: 1, r: 6, facing: 2.6, pose: 'stand' },
              { who: 'hero', q: 0, r: 6, facing: 2.0, pose: 'stand' },
              { who: 'ivar', q: 3, r: -6, facing: 1.6, pose: 'stand' },
              { who: 'hunter', q: 5, r: -7, facing: 3.4, pose: 'fight' },
            ],
          },
        },
        {
          // The ice that moves: the shore and three floes joining and parting, the cracks marked first (§3, act 1).
          kind: 'fight',
          id: 'floes',
          mode: 'floes',
          place: 'islaFloes',
          weather: 'wind',
          budgetBp: 10000,
          goal: { kind: 'floes' },
          npcs: [
            { id: 'camp', does: 'wait', hits: 25, safe: true, vital: true, prop: true },
            { id: 'bera', does: 'wait', hits: 30, safe: true, vital: false },
            { id: 'terron', does: 'wait', hits: 24, safe: true, vital: false },
            { id: 'sten', does: 'wait', hits: 24, safe: true, vital: false },
          ],
          says: [
            { on: 'start', who: 'bera', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'crack1', who: 'bera', line: 'l2' },
            { on: 'cue', cue: 'fall1', who: 'bera', line: 'l3', delay: sec(1) },
            { on: 'cue', cue: 'colossus1', who: 'bera', line: 'l4', delay: sec(2) },
            { on: 'cue', cue: 'part1', who: 'sten', line: 'l5' },
            { on: 'cue', cue: 'drown1', who: 'bera', line: 'l6', delay: sec(1) },
            { on: 'cue', cue: 'band2', who: 'bera', line: 'l7', delay: sec(1) },
          ],
        },
        {
          // What walked under the ice: the clear lagoon, Sigrun's six tracks under it, which walks over which (§4).
          kind: 'fight',
          id: 'lagoon',
          mode: 'lagoon',
          place: 'islaLagoon',
          budgetBp: 10000,
          goal: { kind: 'lagoon' },
          npcs: [
            { id: 'bera', does: 'follow', hits: 30, safe: true, vital: false, follow: 2.2 },
            { id: 'terron', does: 'follow', hits: 24, safe: true, vital: false, follow: 1.4 },
          ],
          says: [
            { on: 'start', who: 'bera', line: 'l1', delay: sec(2) },
            { on: 'at', t: sec(10), who: 'bera', line: 'l2' },
            { on: 'cue', cue: 'seen1', who: 'bera', line: 'l3', delay: sec(1) },
            { on: 'cue', cue: 'pack1', who: 'bera', line: 'l4' },
            { on: 'cue', cue: 'seen4', who: 'bera', line: 'l5', delay: sec(1) },
            { on: 'cue', cue: 'crack1', who: 'bera', line: 'l6', delay: sec(1) },
            { on: 'cue', cue: 'sunk1', who: 'bera', line: 'l7', delay: sec(1) },
            { on: 'cue', cue: 'found', who: 'bera', line: 'l8' },
          ],
        },
        {
          // Under the broken ice: a bag of hide with Sigrun's diary and a folded wolf's skin; her axe, driven into the
          // ice below, as she left it. The diary's lines written on the screen.
          kind: 'scene',
          id: 'diary',
          lines: [ln('l1', 'narrator'), ln('l2', 'diary'), ln('l3', 'diary'), ln('l4', 'bera'), ln('l5', 'narrator')],
          cine: {
            place: 'islaLagoon',
            sky: 'dusk',
            cam: [
              { from: [-1.4, 2.4, 2.2], at: [0.2, -0.2, 0.0], ms: 9000 },
              { from: [1.6, 1.4, 1.1], at: [-0.4, -0.2, 0.3], ms: 9000 },
            ],
            figures: [
              { who: 'bera', q: -1, r: 1, facing: 5.6, pose: 'kneel' },
              { who: 'terron', q: -2, r: 1, facing: 0.2, pose: 'stand' },
              { who: 'hero', q: 1, r: 0, facing: 3.2, pose: 'kneel' },
            ],
          },
        },
        {
          // The fire under the aurora, with Bera alone (Sten gone down to the boat), before she reads the last page.
          kind: 'fire',
          id: 'fire',
          with: ['bera', 'terron'],
          questions: [
            { id: 'page', lines: [ln('l1', 'bera'), ln('l2', 'bera')], set: ['askedLastPage'] },
            {
              id: 'words',
              lines: [ln('l1', 'bera', { notFlag: 'askedSentBack' }), ln('l2', 'bera', { flag: 'askedSentBack' }), ln('l3', 'bera')],
              set: ['askedLastWords'],
            },
            { id: 'home', lines: [ln('l1', 'bera')], set: ['askedBeraHome'] },
          ],
          cine: {
            place: 'islaFire',
            sky: 'night',
            cam: [
              { from: [2.6, 3.4, 1.9], at: [-0.3, -0.4, 0.6], ms: 9000 },
              { from: [-1.8, 3.0, 1.1], at: [0.4, -1.6, 1.4], ms: 9000 },
            ],
            figures: [
              { who: 'bera', q: -1, r: 1, facing: 5.3, pose: 'sit' },
              { who: 'terron', q: -2, r: 1, facing: 0.2, pose: 'lie' },
              { who: 'hero', q: 1, r: -1, facing: 2.2, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          // The memory (docs/SENDAS.md, emotion, piece 9): played as Sigrun, seventy years back, the thirty-first night,
          // her camp whole, the aurora, the pack's eyes out there and the Wolf circling in the water (§3, act 3).
          kind: 'fight',
          id: 'night',
          mode: 'seal',
          place: 'islaNight',
          night: true,
          budgetBp: 10000,
          memory: 'sigrun',
          goal: { kind: 'seal' },
          npcs: [{ id: 'fire', does: 'wait', hits: 25, safe: true, vital: true, prop: true }],
          says: [
            { on: 'start', who: 'sigrun', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'slide1', who: 'sigrun', line: 'l2', delay: sec(1) },
            { on: 'cue', cue: 'dive1', who: 'sigrun', line: 'l3', delay: sec(1) },
            { on: 'half', who: 'sigrun', line: 'l4' },
            { on: 'cue', cue: 'cut1', who: 'sigrun', line: 'l5' },
          ],
          trial: true,
        },
        {
          // The memory's end: dawn; the Wolf swims off toward a black rock on the horizon; Sigrun writes a line, folds the
          // page and walks over the lagoon alone, toward the water. She isn't seen coming back.
          kind: 'scene',
          id: 'dawn',
          lines: [ln('l1', 'narrator'), ln('l2', 'narrator')],
          cine: {
            place: 'islaNight',
            sky: 'dawn',
            memory: true,
            cam: [
              { from: [-2.0, 6.0, 1.6], at: [1.0, -6.0, 0.6], ms: 9000 },
              { from: [0.6, 2.4, 1.0], at: [0.0, -9.0, 0.6], ms: 9000 },
            ],
            figures: [{ who: 'sigrun', q: 0, r: 1, facing: 4.5, pose: 'walk', speed: 14, walkMs: 16000 }],
          },
        },
        {
          // The present: the last page read aloud; Ivar over the lagoon with his hunters; the boats taken, and Sten.
          kind: 'scene',
          id: 'ivar',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'bera'),
            ln('l3', 'narrator'),
            ln('l4', 'ivar'),
            ln('l5', 'narrator'),
            ln('l6', 'ivar'),
            ln('l7', 'sten'),
            ln('l8', 'ivar'),
            ln('l9', 'narrator'),
            ln('l10', 'narrator'),
          ],
          cine: {
            place: 'islaLagoon',
            sky: 'dusk',
            cam: [
              { from: [-2.6, 5.0, 2.6], at: [0.4, -0.4, 0.6], ms: 9000 },
              { from: [4.3, 5.4, 2.2], at: [-0.8, -2.4, 0.7], ms: 9000 },
              { from: [0.0, 3.0, 5.9], at: [0.0, 12.0, -0.4], ms: 9000 },
            ],
            figures: [
              { who: 'bera', q: 0, r: 0, facing: 4.5, pose: 'stand' },
              { who: 'terron', q: -1, r: 1, facing: 4.6, pose: 'stand' },
              { who: 'hero', q: 1, r: 1, facing: 4.0, pose: 'stand' },
              { who: 'ivar', q: 0, r: -3, facing: 1.5, pose: 'stand' },
              { who: 'hunter', q: 1, r: -4, facing: 1.6, pose: 'stand' },
              { who: 'hunter2', q: -1, r: -3, facing: 1.4, pose: 'stand' },
              { who: 'sten', q: 2, r: -2, facing: 2.6, pose: 'stand' },
            ],
          },
        },
        {
          // The lowest point: the boats gone, Bera with the last page in her hand, Sigrun's axe driven into the ice.
          kind: 'choice',
          id: 'axe',
          lines: [ln('l1', 'bera'), ln('l2', 'bera')],
          options: [
            { id: 'take', set: ['axeTaken'] },
            { id: 'leave', set: ['axeLeft'] },
          ],
          cine: {
            place: 'islaLagoon',
            sky: 'dusk',
            cam: [
              { from: [-1.7, 2.8, 2.4], at: [0.3, -0.1, 0.25], ms: 9000 },
              { from: [2.2, 2.4, 1.8], at: [-0.2, 0.0, 0.3], ms: 9000 },
            ],
            figures: [
              { who: 'bera', q: -1, r: 1, facing: 5.6, pose: 'sit' },
              { who: 'terron', q: -2, r: 1, facing: 0.2, pose: 'lie' },
              { who: 'hero', q: 1, r: 0, facing: 3.2, pose: 'stand' },
            ],
          },
        },
        {
          // The close: the axe taken or covered with stones; Bera stays; Terrón to the hero; at dawn, Sten alone in the
          // small boat, rowing without stopping.
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'narrator', { flag: 'axeTaken' }),
            ln('l2', 'bera', { flag: 'axeTaken' }),
            ln('l3', 'narrator', { flag: 'axeLeft' }),
            ln('l4', 'bera', { flag: 'axeLeft' }),
            ln('l5', 'bera'),
            ln('l6', 'bera'),
            ln('l7', 'narrator'),
            ln('l8', 'bera'),
            ln('l9', 'narrator'),
            ln('l10', 'sten'),
            ln('l11', 'sten'),
            ln('l12', 'narrator'),
            ln('l13', 'sten', { notFlag: 'ropeDropped' }),
            ln('l14', 'sten', { flag: 'ropeDropped' }),
            ln('l15', 'sten', { flag: 'ropeDropped' }),
            ln('l16', 'narrator'),
          ],
          cine: {
            place: 'islaShore',
            sky: 'dawn',
            cam: [
              { from: [0.0, 1.6, 3.2], at: [1.8, 5.2, 0.45], ms: 9000 },
              { from: [4.4, 2.4, 2.4], at: [1.6, 5.2, 0.45], ms: 9000 },
              { from: [-5.0, 3.5, 4.4], at: [4.0, 14.0, -0.4], ms: 9000 },
            ],
            figures: [
              { who: 'bera', q: -1, r: 5, facing: 0.6, pose: 'sit' },
              { who: 'terron', q: 0, r: 5, facing: 3.6, pose: 'stand' },
              { who: 'hero', q: 0, r: 6, facing: 4.8, pose: 'stand' },
              { who: 'sten', q: 3, r: 9, facing: 2.4, pose: 'stand' },
            ],
          },
        },
      ],
    },
    ROCK_CHAPTER,
  ],
};
